import React, { ForwardedRef } from 'react';
import styles from '@danske/sapphire-css/components/table/table.module.css';
import { useMessageFormatter } from '@react-aria/i18n';
import { IconButton, IconButtonProps } from '../../IconButton';
import { ChevronDown } from '@danske/sapphire-icons/react';
import intlMessages from '../i18n';
import { useTableRowContext } from './TableRowContext';
import clsx from 'clsx';
import { PressEvent } from '@react-types/shared/src/events';

export interface RowExpandToggleCellProps {
  /**
   * Note: Currently, we don't officially support non-table elements.
   *
   * @default 'td'
   */
  elementType?: 'td';
  isExpanded?: boolean;

  expandButtonProps?: Pick<IconButtonProps<'button'>, 'onPress'>;
}

/**
 * Cell rendered in {@link Table.Row} to toggle the expanded state of the row.
 */
export const RowExpandToggleCell = React.forwardRef(
  function RowExpandToggleButton(
    { isExpanded, elementType, expandButtonProps }: RowExpandToggleCellProps,
    ref: ForwardedRef<HTMLTableCellElement>
  ) {
    const ElementType = elementType || 'td';

    const { expandedViewId } = useTableRowContext();

    return (
      <ElementType ref={ref}>
        <ToggleExpandButton
          expandedViewId={expandedViewId}
          isExpanded={isExpanded}
          onPress={expandButtonProps?.onPress}
        />
      </ElementType>
    );
  }
);

export function ToggleExpandButton({
  expandedViewId,
  isExpanded,
  onPress,
}: {
  expandedViewId?: string;
  isExpanded: boolean | undefined;
  onPress: ((e: PressEvent) => void) | undefined;
}): React.JSX.Element {
  const formatMessage = useMessageFormatter(intlMessages);
  return (
    <span className={clsx(styles['sapphire-table__row-expand-button'])}>
      <IconButton
        variant="tertiary"
        aria-expanded={isExpanded}
        aria-controls={isExpanded ? expandedViewId : undefined}
        aria-label={
          isExpanded ? formatMessage('collapse') : formatMessage('expand')
        }
        onPress={onPress}
      >
        <ChevronDown />
      </IconButton>
    </span>
  );
}
